import { parseCoordinates, parseShortPlusCode, recoverPlusCode } from './coordinates';
import { photonSearch } from './geocoding/photon';
import type { SearchItem, SearchViewport } from './types';

const MAX_RESULTS = 10;

/** Turns the query into up to 10 suggestions: parsed coordinates, a recovered Plus Code, or places. */
export async function resolveQuery(
  q: string,
  viewport: SearchViewport,
  signal: AbortSignal,
): Promise<SearchItem[]> {
  const c = parseCoordinates(q);
  if (c) {
    const name = `${c.lat.toFixed(6)}, ${c.lng.toFixed(6)}`;
    return [
      { name, lat: c.lat, lng: c.lng, exact: true, details: { kind: 'coordinates', format: c.format } },
    ];
  }

  const short = parseShortPlusCode(q);
  if (short) {
    // Short Plus Codes are relative to a reference location: the named locality, else the map centre.
    let ref = { lat: viewport.lat, lng: viewport.lng };
    let near: string | null = null;
    if (short.locality) {
      const [place] = await photonSearch({ q: short.locality, limit: 1, viewport, signal, placesOnly: true });
      if (!place) return [];
      ref = place;
      near = place.name;
    }
    const { code, lat, lng } = recoverPlusCode(short.code, ref);
    return [{ name: code, lat, lng, exact: true, details: { kind: 'plusCode', near } }];
  }

  if (q.length < 2) return [];
  const seen = new Set<string>();
  const places = await photonSearch({ q, limit: MAX_RESULTS, viewport, signal });
  return places.filter((it) => {
    const key = `${it.name}|${it.details.kind === 'place' ? it.details.text : ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
